import { sortBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import {
  SavedReportSettingsConfig,
  SavedReportSettingsPrivacyType,
} from '~/data/reportSettings';
import { UseSavedReportSettingsConfigOutput } from '~/scenes/PortalModules/Analytics/common';
import {
  IncludedSavedSettings,
  canSaveSharedSettingsUserRoles,
  getIncludedSavedSettings,
} from '~/scenes/PortalModules/Analytics/common/SavedReportSettingsSelector/data';
import { hasOneOfRoles } from '~/services/Permissions';

type SavedSettingOption = {
  id: string;
  name: string;
  category: string;
  secondary?: string;
  savedSettings?: IncludedSavedSettings;
};

type PropsType<P> = Pick<
  UseSavedReportSettingsConfigOutput<P>,
  'selectedReportSettings' | 'reportSettingOptions'
>;

export const useSavedReportSettingsSelector = <P>(props: PropsType<P>) => {
  const { selectedReportSettings, reportSettingOptions } = props;

  const { t } = useTranslation();

  const myRoles = useLoggedInUserRoles();

  const { canEditSelectedSettings, selectedReportId } = useMemo(() => {
    const defaultSettingsId = 'default';
    const selectedReportId = selectedReportSettings?.id ?? defaultSettingsId;

    const isNotDefaultSettings = selectedReportId !== defaultSettingsId;

    const isSelectedPersonal =
      selectedReportSettings?.privacyType ===
      SavedReportSettingsPrivacyType.PERSONAL;
    const canEditByRole = hasOneOfRoles(
      myRoles,
      canSaveSharedSettingsUserRoles,
    );

    return {
      canEditSelectedSettings:
        isNotDefaultSettings && (isSelectedPersonal || canEditByRole),
      selectedReportId,
    };
  }, [myRoles, selectedReportSettings]);

  const getSavedSettingsDetails = useCallback(
    (settingsConfig?: SavedReportSettingsConfig<P>) => {
      if (!!settingsConfig) {
        const savedSettings = getIncludedSavedSettings(settingsConfig);

        const savedSettingsTranslations = Object.entries(savedSettings)
          .filter((_key, value) => value)
          .map(([key]): string => {
            switch (key) {
              case 'reportSelection':
                return t('dataset');
              case 'columnVisibility':
                return t('columnVisibilty');
              case 'columnSorting':
                return t('columnSorting');
              case 'columnFiltering':
                return t('columnFiltering');
              default:
                return '';
            }
          });

        return savedSettingsTranslations.join(', ');
      }

      return undefined;
    },
    [t],
  );

  const options = useMemo((): {
    all: SavedSettingOption[];
    default: SavedSettingOption[];
    shared: SavedSettingOption[];
    personal: SavedSettingOption[];
  } => {
    const allOptions = [
      {
        id: 'default',
        name: t('laDefaultReportSettings'),
        category: 'DEFAULT',
      },
    ].concat(
      sortBy(
        reportSettingOptions.map(
          ({ id, name, settingsConfig, privacyType }): SavedSettingOption => ({
            id,
            name,
            category: privacyType,
            secondary: getSavedSettingsDetails(settingsConfig),
            savedSettings: getIncludedSavedSettings(settingsConfig),
          }),
        ),
        ['category', 'name'],
      ),
    );

    return {
      all: allOptions,
      default: allOptions.filter((o) => o.category === 'DEFAULT'),
      shared: allOptions.filter(
        (o) => o.category === SavedReportSettingsPrivacyType.SHARED,
      ),
      personal: allOptions.filter(
        (o) => o.category === SavedReportSettingsPrivacyType.PERSONAL,
      ),
    };
  }, [reportSettingOptions, getSavedSettingsDetails, t]);

  const selectField = {
    label: t('laCurrentReportSettings'),
    labelId: 'saved-settings-label',
  };

  return {
    selectField,
    options,
    canEditSelectedSettings,
    selectedReportId,
  };
};
